import {slugify} from './helpers';
import Storage from './storage';

export const CURRENT_SELECTION_KEY = 'seatshop-current-selection';
export const CONFIGURATION_SKIPPED_TOAST_KEY = 'seatshop-configuration-skipped-toast';

export const VEHICLE_KEYS = ['year', 'make', 'model', 'submodel'];
export const FITMENT_KEYS = ['cab_configuration', 'seat_configuration_1'];
export const MATERIAL_KEYS = [
    'material_configuration',
    'color',
    'insert',
    'pattern',
    'part',
    'seat_position',
    'style',
    'piping'
];

const labelMap = {
    cab_configuration: 'Cab Type',
    seat_configuration_1: 'Seat Style',
    material_configuration: 'Material',
    color: 'Color',
    insert: 'Insert',
    pattern: 'Pattern',
    part: 'Part',
    seat_position: 'Seat Position',
    style: 'Style',
    piping: 'Piping'
};

const present = value => value != null && value !== '' && value !== 'null';

const titleCase = value => String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());

export const getVehicleLabel = params => VEHICLE_KEYS
    .map(key => params?.[key])
    .filter(present)
    .join(' ');

export const getHandle = params => slugify(
    VEHICLE_KEYS.map(key => params?.[key]).filter(present).join('-')
);

const splitSelections = params => {
    const selections = {};
    params.forEach((value, key) => {
        if (!VEHICLE_KEYS.includes(key) && !['results', 'foam', 'slug', 'configuration_skipped'].includes(key) && present(value)) {
            selections[key] = value;
        }
    });
    return selections;
};

const buildRows = selection => {
    const selections = selection?.selections || {};
    const standardSteps = selection?.standardSteps || [];
    const fitment = FITMENT_KEYS.map(key => selections[key]).filter(present);
    const material = MATERIAL_KEYS.map(key => selections[key]).filter(present);

    Object.keys(selections).forEach(key => {
        if (!FITMENT_KEYS.includes(key) && !MATERIAL_KEYS.includes(key) && present(selections[key])) {
            material.push(selections[key]);
        }
    });

    return [
        {
            key: 'vehicle',
            label: 'Vehicle',
            value: getVehicleLabel(selection?.vehicleParams),
            step: 'vehicle'
        },
        {
            key: 'fitment',
            label: 'Fitment',
            value: standardSteps.includes('fitment') ? 'Standard layout' : fitment.join(' / '),
            step: 'fitment',
            disabled: standardSteps.includes('fitment')
        },
        {
            key: 'material',
            label: 'Material',
            value: standardSteps.includes('material') ? 'Standard layout' : material.join(' / '),
            step: 'material',
            disabled: standardSteps.includes('material')
        }
    ].filter(row => present(row.value));
};

export const normalizeSelection = input => {
    if (!input?.vehicleParams || !VEHICLE_KEYS.every(key => present(input.vehicleParams[key]))) {
        return null;
    }

    const normalized = {
        vehicleParams: {...input.vehicleParams},
        selections: {...(input.selections || {})},
        standardSteps: Array.isArray(input.standardSteps) ? [...input.standardSteps] : [],
        foam: input.foam === true || input.foam === 'true',
        handle: input.handle || getHandle(input.vehicleParams)
    };

    normalized.vehicleLabel = getVehicleLabel(normalized.vehicleParams);
    normalized.rows = buildRows(normalized);
    return normalized;
};

export const getSelectionFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const vehicleParams = {};

    VEHICLE_KEYS.forEach(key => {
        vehicleParams[key] = params.get(key);
    });

    return normalizeSelection({
        vehicleParams,
        selections: splitSelections(params),
        standardSteps: params.get('configuration_skipped') === 'true' ? ['fitment', 'material'] : [],
        foam: params.get('foam') === 'true',
        handle: window.location.pathname.includes('/collections/')
            ? window.location.pathname.split('/collections/')[1]?.split('/')[0]
            : null
    });
};

export const getStoredSelection = () => {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) return null;
        return normalizeSelection(JSON.parse(window.sessionStorage.getItem(CURRENT_SELECTION_KEY)));
    } catch (error) {
        return null;
    }
};

export const getCurrentSelection = () => {
    const urlSelection = getSelectionFromUrl();
    const storedSelection = getStoredSelection();

    if (
        urlSelection
        && storedSelection?.handle === urlSelection.handle
        && storedSelection.standardSteps.length
    ) {
        return normalizeSelection({
            ...urlSelection,
            standardSteps: storedSelection.standardSteps
        });
    }

    return urlSelection || storedSelection;
};

export const getCollectionUrl = input => {
    const selection = normalizeSelection(input);
    if (!selection?.handle) return null;

    const queryString = new URLSearchParams({
        results: '1',
        ...selection.vehicleParams,
        foam: String(selection.foam)
    });

    Object.keys(selection.selections).forEach(key => {
        const value = selection.selections[key];
        if (present(value)) {
            queryString.set(key, value);
        }
    });

    if (selection.standardSteps.length) {
        queryString.set('configuration_skipped', 'true');
    }

    return `/collections/${encodeURIComponent(selection.handle)}/?${queryString.toString()}`;
};

export const storeCurrentSelection = selection => {
    let selectionToStore = selection;

    try {
        const stored = JSON.parse(window.sessionStorage.getItem(CURRENT_SELECTION_KEY));
        const sameHandle = stored?.handle && stored.handle === selection?.handle;
        const omittedStandardSteps = !Object.prototype.hasOwnProperty.call(selection || {}, 'standardSteps');

        if (sameHandle && omittedStandardSteps && Array.isArray(stored.standardSteps)) {
            selectionToStore = {
                ...selection,
                standardSteps: stored.standardSteps
            };
        }
    } catch (error) {}

    const normalized = normalizeSelection(selectionToStore);
    if (!normalized) return null;
    try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.setItem(CURRENT_SELECTION_KEY, JSON.stringify(normalized));
        }
    } catch (error) {}
    return normalized;
};

export const clearCurrentSelection = selection => {
    const active = normalizeSelection(selection) || getCurrentSelection();
    try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.removeItem(CURRENT_SELECTION_KEY);
            window.sessionStorage.removeItem('last-vehicle-selection');
        }
    } catch (error) {}

    if (active?.handle) {
        Storage.removeSelections(active.handle);
    }
};

export const filterSelectionsForStep = (selection, step) => {
    const selections = {...(selection?.selections || {})};

    if (step === 'vehicle' || step === 'fitment') {
        return {};
    }

    if (step === 'material') {
        return FITMENT_KEYS.reduce((carry, key) => {
            if (present(selections[key])) {
                carry[key] = selections[key];
            }
            return carry;
        }, {});
    }

    return selections;
};

export const rowLabel = key => labelMap[key] || titleCase(key);
